import { useMemo, useState } from "react";
import { useMutation, useQuery } from "convex/react";
import { useNavigate } from "react-router-dom";
import Papa from "papaparse";
import * as XLSX from "xlsx";
import { api } from "../../convex/_generated/api";
import { newIdempotencyId, parseConvexError } from "../lib/utils";
import { parseClipboardMatrix } from "../lib/gridOps";
import type { Id } from "../../convex/_generated/dataModel";

type DraftRow = {
  ean: string;
  asin: string;
  itemName: string;
  quantity: string;
  unitCost: string;
};

const emptyRow = (): DraftRow => ({ ean: "", asin: "", itemName: "", quantity: "1", unitCost: "" });

export function NewOrderPage() {
  const create = useMutation(api.orders.create);
  const createSupplier = useMutation(api.suppliers.create);
  const suppliers = useQuery(api.suppliers.list, {});
  const locations = useQuery(api.locations.list);
  const navigate = useNavigate();

  const [title, setTitle] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [newSupplier, setNewSupplier] = useState("");
  const [poNumber, setPoNumber] = useState("");
  const [notes, setNotes] = useState("");
  const [expectedAt, setExpectedAt] = useState("");
  const [shipToLocationId, setShipToLocationId] = useState("");
  const [rows, setRows] = useState<DraftRow[]>([emptyRow(), emptyRow(), emptyRow()]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const validRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          (r.ean.trim() || r.asin.trim() || r.itemName.trim()) &&
          Number.isInteger(Number(r.quantity)) &&
          Number(r.quantity) > 0,
      ),
    [rows],
  );

  const applyMatrix = (matrix: string[][], startRow = 0) => {
    setRows((prev) => {
      const next = [...prev];
      while (next.length < startRow + matrix.length) next.push(emptyRow());
      for (let i = 0; i < matrix.length; i++) {
        const src = matrix[i] ?? [];
        const target = { ...(next[startRow + i] ?? emptyRow()) };
        if (src[0] != null) target.ean = src[0];
        if (src[1] != null) target.asin = src[1];
        if (src[2] != null) target.itemName = src[2];
        if (src[3] != null) target.quantity = src[3] || "1";
        if (src[4] != null) target.unitCost = src[4];
        next[startRow + i] = target;
      }
      return next;
    });
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>New order</h1>
          <p className="lede">
            Create a purchase order with lines — paste from Sheets, upload a file, or type rows.
          </p>
        </div>
      </div>

      <section className="panel">
        <h2 className="panel-title">Header</h2>
        <label className="field">
          <span>Title</span>
          <input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="PO name" />
        </label>
        <div className="header-fields">
          <label className="field">
            <span>Supplier</span>
            <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)}>
              <option value="">Select…</option>
              {(suppliers ?? []).map((s) => (
                <option key={s._id} value={s._id}>
                  {s.name}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Or create supplier</span>
            <input
              value={newSupplier}
              onChange={(e) => setNewSupplier(e.target.value)}
              placeholder="New supplier name"
            />
          </label>
        </div>
        <div className="header-fields">
          <label className="field">
            <span>PO number</span>
            <input value={poNumber} onChange={(e) => setPoNumber(e.target.value)} />
          </label>
          <label className="field">
            <span>Expected date</span>
            <input type="date" value={expectedAt} onChange={(e) => setExpectedAt(e.target.value)} />
          </label>
          <label className="field">
            <span>Ship to</span>
            <select value={shipToLocationId} onChange={(e) => setShipToLocationId(e.target.value)}>
              <option value="">Default</option>
              {(locations ?? []).map((l) => (
                <option key={l._id} value={l._id}>
                  {l.name}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span>Notes</span>
          <input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </label>
      </section>

      <section className="panel" style={{ marginTop: "0.85rem" }}>
        <div className="page-header" style={{ marginBottom: "0.5rem" }}>
          <h2 className="panel-title" style={{ margin: 0 }}>
            Lines ({validRows.length} ready)
          </h2>
          <div className="header-actions">
            <label className="btn compact">
              Upload CSV/XLSX
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                hidden
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (!file) return;
                  const reader = new FileReader();
                  reader.onload = () => {
                    try {
                      if (file.name.endsWith(".csv")) {
                        const parsed = Papa.parse<string[]>(String(reader.result ?? ""), {
                          skipEmptyLines: true,
                        });
                        applyMatrix((parsed.data as string[][]).slice(1));
                      } else {
                        const wb = XLSX.read(reader.result, { type: "array" });
                        const sheet = wb.Sheets[wb.SheetNames[0]!];
                        const data = XLSX.utils.sheet_to_json<string[]>(sheet, {
                          header: 1,
                          defval: "",
                        });
                        applyMatrix(data.slice(1).map((r) => r.map(String)));
                      }
                    } catch (err) {
                      setError(err instanceof Error ? err.message : "Failed to parse file");
                    }
                  };
                  if (file.name.endsWith(".csv")) reader.readAsText(file);
                  else reader.readAsArrayBuffer(file);
                }}
              />
            </label>
            <button
              type="button"
              className="btn compact"
              onClick={() => setRows((r) => [...r, emptyRow(), emptyRow()])}
            >
              Add rows
            </button>
          </div>
        </div>
        <p className="muted tiny">
          Columns: EAN · ASIN · Item name · Qty · Cost. Paste TSV into the grid area.
        </p>
        <div
          className="table-scroll"
          onPaste={(e) => {
            const text = e.clipboardData.getData("text/plain");
            if (!text.includes("\t") && !text.includes("\n")) return;
            e.preventDefault();
            applyMatrix(parseClipboardMatrix(text));
          }}
        >
          <table className="data-table">
            <thead>
              <tr>
                <th>EAN</th>
                <th>ASIN</th>
                <th>Item</th>
                <th>Qty</th>
                <th>Cost</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  {(["ean", "asin", "itemName", "quantity", "unitCost"] as const).map((key) => (
                    <td key={key}>
                      <input
                        className="rdg-text-editor"
                        style={{ width: "100%", border: 0, background: "transparent" }}
                        value={row[key]}
                        onChange={(e) => {
                          const value = e.target.value;
                          setRows((prev) =>
                            prev.map((r, i) => (i === idx ? { ...r, [key]: value } : r)),
                          );
                        }}
                      />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {error && <p className="error">{error}</p>}

      <div className="header-actions" style={{ marginTop: "1rem" }}>
        <button
          type="button"
          className="btn primary"
          disabled={!title.trim() || busy}
          onClick={() => {
            void (async () => {
              try {
                setBusy(true);
                setError(null);
                let sid = supplierId || undefined;
                if (!sid && newSupplier.trim()) {
                  const created = await createSupplier({
                    name: newSupplier.trim(),
                    idempotencyId: newIdempotencyId("sup"),
                  });
                  sid = created.supplierId;
                }
                const result = await create({
                  title: title.trim(),
                  supplierId: sid as Id<"suppliers"> | undefined,
                  poNumber: poNumber.trim() || undefined,
                  notes: notes.trim() || undefined,
                  expectedAt: expectedAt ? new Date(expectedAt).getTime() : undefined,
                  shipToLocationId: shipToLocationId
                    ? (shipToLocationId as Id<"locations">)
                    : undefined,
                  rows: validRows.map((r) => ({
                    ean: r.ean.trim() || undefined,
                    asin: r.asin.trim() || undefined,
                    itemName: r.itemName.trim() || undefined,
                    quantity: Number(r.quantity),
                    unitCost: r.unitCost ? Number(r.unitCost) : undefined,
                  })),
                  idempotencyId: newIdempotencyId("create"),
                });
                navigate(`/orders/${result.orderId}`);
              } catch (err) {
                setError(parseConvexError(err).message);
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          {busy ? "Creating…" : "Create order"}
        </button>
      </div>
    </div>
  );
}
